import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, ArrowRight, Shield, Check, Lock, MapPin, DollarSign, Clock, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { captureLead, trackEvent, getNeighbourhood } from '../lib/api';
import { GA_EVENTS } from '../lib/analytics';
import { formatPrice } from '../lib/format';
import SEO from '../components/SEO';

function ScoreRing({ score, size = 56 }) {
  const radius = (size - 8) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth="5" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.16, 1, 0.3, 1)' }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono text-sm font-bold text-slate-900">{score}</span>
    </div>
  );
}

export default function LeadCapture() {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [enriched, setEnriched] = useState({});
  const [form, setForm] = useState({ first_name: '', email: '', phone: '', marketing_opt_in: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('blueprint_results');
    if (!stored) { navigate('/blueprint'); return; }
    const parsed = JSON.parse(stored);
    setResults(parsed);
    trackEvent({ event_type: 'results_preview_viewed', page: 'capture' });
    GA_EVENTS.captureFormViewed();

    // Fetch enriched data for top matches
    (parsed.neighbourhood_scores || []).slice(0, 3).forEach(m => {
      if (m.slug) {
        getNeighbourhood(m.slug).then(r => setEnriched(prev => ({ ...prev, [m.slug]: r.data }))).catch(() => {});
      }
    });
  }, [navigate]);

  if (!results) return null;

  const matches = results.neighbourhood_scores || [];
  const topMatch = matches[0];
  const explanation = results.explanation;

  const validate = () => {
    const errs = {};
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Valid email required';
    if (!form.marketing_opt_in) errs.consent = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const sessionToken = localStorage.getItem('blueprint_session');
    if (!sessionToken) { navigate('/blueprint'); return; }
    try {
      const r = await captureLead({ session_token: sessionToken, ...form });
      trackEvent({ event_type: 'lead_captured', session_token: sessionToken });
      GA_EVENTS.leadCaptured(r.data?.intent?.tier || 'unknown');
      localStorage.setItem('lead_result', JSON.stringify(r.data));
      localStorage.removeItem('blueprint_session');
      navigate('/results');
    } catch (e) {
      setErrors({ submit: 'Something went wrong. Please try again.' });
    }
    setSubmitting(false);
  };

  const handleSkip = () => {
    trackEvent({ event_type: 'capture_skipped', page: 'capture' });
    setSkipped(true);
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="lead-capture-page">
      <SEO title="Your Burlington Neighbourhood Matches" noindex={true} />

      <div className="max-w-4xl mx-auto px-6 py-10 md:py-16">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200/50 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs text-emerald-700 font-medium">Your analysis is ready</span>
          </div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Your Burlington <span className="italic">neighbourhood matches</span>
          </h1>
          {explanation && <p className="text-base text-slate-500 max-w-xl mx-auto leading-relaxed">{explanation}</p>}
        </div>

        {/* ─── VISIBLE RESULTS (70-80%) ─── */}
        <div className="space-y-5 mb-10">
          {matches.slice(0, 3).map((match, i) => {
            const detail = enriched[match.slug] || {};
            const isLocked = !skipped && i > 0 && !localStorage.getItem('lead_result');

            return (
              <div key={match.name} className={`bg-white border rounded-2xl overflow-hidden ${isLocked ? 'border-slate-200/60' : 'border-slate-200'}`} data-testid={`match-card-${i}`}>
                {/* Card header — always visible */}
                <div className="p-5 md:p-6">
                  <div className="flex items-start gap-4">
                    <ScoreRing score={match.score} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-slate-400">#{i + 1} Match</span>
                        {i === 0 && <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Best Fit</span>}
                      </div>
                      <h3 className="text-xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>{match.name}</h3>
                      <p className="text-[13px] text-slate-500 mt-1">{match.vibe_summary}</p>
                    </div>
                  </div>

                  {/* Why it fits — visible for all */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                    {match.reasons?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-widest mb-2">Why it fits</p>
                        <div className="space-y-1.5">
                          {match.reasons.map((r, j) => (
                            <div key={j} className="flex items-start gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span className="text-[13px] text-slate-600">{r}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {match.tradeoffs?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-widest mb-2">Tradeoffs</p>
                        <div className="space-y-1.5">
                          {match.tradeoffs.map((t, j) => (
                            <div key={j} className="flex items-start gap-2">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                              <span className="text-[13px] text-slate-600">{t}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Premium content — locked unless submitted or skipped */}
                {isLocked ? (
                  <div className="relative">
                    <div className="p-5 pt-0 filter blur-[6px] select-none pointer-events-none">
                      <div className="grid grid-cols-3 gap-3 py-3 border-t border-slate-100">
                        <div className="text-center"><p className="text-[10px] text-slate-400">Detached</p><p className="font-mono text-xs text-slate-600">$1.2M–$1.8M</p></div>
                        <div className="text-center"><p className="text-[10px] text-slate-400">Town</p><p className="font-mono text-xs text-slate-600">$800K–$1.1M</p></div>
                        <div className="text-center"><p className="text-[10px] text-slate-400">Schools</p><p className="font-mono text-xs text-slate-600">Top rated</p></div>
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button onClick={() => setShowForm(true)} className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl px-4 py-2 flex items-center gap-2 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                        <Lock className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-700">Unlock full details</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Unlocked premium content */
                  <div className="px-5 pb-5 space-y-3">
                    {/* Price ranges */}
                    {detail.price_bands && (
                      <div className="grid grid-cols-3 gap-3 py-3 border-t border-slate-100">
                        {[
                          { label: 'Detached', band: detail.price_bands?.detached },
                          { label: 'Town', band: detail.price_bands?.town },
                          { label: 'Condo', band: detail.price_bands?.condo },
                        ].map(item => (
                          <div key={item.label} className="text-center">
                            <p className="text-[10px] text-slate-400">{item.label}</p>
                            {item.band?.min > 0 ? (
                              <p className="font-mono text-xs font-medium text-slate-700">{formatPrice(item.band.min)}–{formatPrice(item.band.max)}</p>
                            ) : <p className="text-xs text-slate-300">N/A</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Schools */}
                    {detail.school_breakdown?.public?.length > 0 && (
                      <div className="pt-2">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Key schools</p>
                        <p className="text-[12px] text-slate-500">{detail.school_breakdown.public[0]}</p>
                      </div>
                    )}
                    {/* Link to guide */}
                    <Link to={`/neighbourhoods/${match.slug}`} className="inline-flex items-center gap-1 text-[12px] text-emerald-600 hover:text-emerald-700 mt-1">
                      Full {match.name} guide <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ─── UNLOCK GATE (Form) ─── */}
        {!localStorage.getItem('lead_result') && !skipped && (
          <div id="unlock-form" className={`transition-all duration-500 ${showForm ? 'opacity-100' : 'opacity-100'}`}>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 max-w-lg mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-xl md:text-2xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  Unlock your full Burlington plan
                </h2>
                <p className="text-sm text-slate-500">
                  Get your complete strategy — pricing, school catchments, and next steps based on your answers.
                </p>
              </div>

              {/* Trust Module */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-emerald-500" />
                  <p className="text-sm font-medium text-slate-800">We hate spam too.</p>
                </div>
                <div className="space-y-1.5">
                  {['No cold calls', 'No pressure', 'No selling your data'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-emerald-500" />
                      <span className="text-xs text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-3 pt-3 border-t border-slate-100">
                  We use your email to send your custom plan and relevant listings. That's it.
                </p>
              </div>

              {/* Minimal Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-[11px] text-slate-400 text-center">Takes 10 seconds. Instant access.</p>

                <div>
                  <Input placeholder="First name" className="bg-white rounded-xl h-12 text-sm border-slate-200" value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="input-first-name" />
                </div>

                <div>
                  <Input type="email" placeholder="Your email address" className={`bg-white rounded-xl h-12 text-sm ${errors.email ? 'border-red-400' : 'border-slate-200'}`} value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="input-email" />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <Input type="tel" placeholder="Phone (optional — for faster listing access)" className="bg-white rounded-xl h-12 text-sm border-slate-200" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="input-phone" />
                </div>

                <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${errors.consent ? 'border-red-300 bg-red-50' : form.marketing_opt_in ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-200 bg-white'}`} data-testid="consent-checkbox">
                  <Checkbox checked={form.marketing_opt_in} onCheckedChange={v => { setForm(f => ({ ...f, marketing_opt_in: v })); if (v) setErrors(e => ({ ...e, consent: undefined })); }} className="sr-only" />
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5 ${form.marketing_opt_in ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                    {form.marketing_opt_in && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-[11px] text-slate-500 leading-relaxed">I'd like to receive my personalized plan and relevant Burlington listings. <a href="/privacy" target="_blank" className="underline">Privacy Policy</a></span>
                </label>
                {errors.consent && <p className="text-xs text-red-500">{errors.consent}</p>}

                {errors.submit && <p className="text-sm text-red-500 text-center">{errors.submit}</p>}

                <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-full py-3.5 h-auto text-base font-semibold shadow-lg shadow-emerald-600/20 hover:-translate-y-px transition-all" data-testid="btn-submit-lead">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unlocking...</> : <>Unlock My Plan <ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>
              </form>

              {/* Soft skip */}
              <div className="text-center mt-4">
                <button onClick={handleSkip} className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline" data-testid="skip-btn">
                  Skip for now — see limited results
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── SKIPPED STATE ─── */}
        {skipped && !localStorage.getItem('lead_result') && (
          <div className="bg-emerald-50 border border-emerald-200/50 rounded-2xl p-6 max-w-lg mx-auto text-center mt-6">
            <p className="text-sm text-emerald-800 font-medium mb-2">Want the full plan?</p>
            <p className="text-xs text-emerald-600 mb-4">Unlock pricing insights, school catchments, and your personalized next steps.</p>
            <button onClick={() => { setSkipped(false); setShowForm(true); }} className="text-sm font-medium text-emerald-700 underline hover:text-emerald-800">
              Unlock full results →
            </button>
          </div>
        )}

        {/* ─── POST-SUBMISSION: Full Results Link ─── */}
        {localStorage.getItem('lead_result') && (
          <div className="text-center mt-8">
            <div className="bg-emerald-50 border border-emerald-200/50 rounded-2xl p-6 max-w-lg mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900 mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Plan unlocked</h3>
              <p className="text-sm text-slate-500 mb-4">Your full personalized plan is ready.</p>
              <Link to="/results">
                <Button className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-8 h-11 text-sm font-semibold">
                  View Full Results <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
